import { z } from 'zod';
import {
  Channel,
  Connection,
  FreeformElement,
  FrameKind,
  Item,
  Mix,
  PowerSource,
} from '../model/schema.js';
import type { BoardData, Endpoint, Frame } from '../model/schema.js';
import { builtinLayerId, defaultLayerKeyFor } from '../model/layers.js';
import { newId } from '../model/ids.js';
import { IN_TRAY, mainStage, newAreaSpot, placeAt } from '../model/areas.js';
import type { Point } from '../model/areas.js';

const finite = z.number().finite();

/**
 * Reusable part of a board, version 1 (decision U16, OIL-13). Item positions are relative to the
 * template centre (stage-local metres), free-form positions too (converted from world
 * coordinates). `frameId: null` means the unplaced tray, any other value the stage.
 */
export const TemplateDataV1 = z.object({
  version: z.literal(1),
  items: z.array(Item),
  connections: z.array(Connection),
  channels: z.array(Channel),
  mixes: z.array(Mix),
  power: z.array(PowerSource),
  freeform: z.array(FreeformElement),
});
export type TemplateDataV1 = z.infer<typeof TemplateDataV1>;

/** `frameId` of template items on the main stage (version 2). */
export const TEMPLATE_MAIN = 'main';

/** A non-main area of a template; `x/y` = origin, main-stage-local, relative to the centre. */
export const TemplateArea = z.object({
  id: z.string().min(1),
  kind: FrameKind,
  name: z.string(),
  width: finite.positive(),
  depth: finite.positive(),
  zones: z.boolean(),
  x: finite,
  y: finite,
});
export type TemplateArea = z.infer<typeof TemplateArea>;

/**
 * Version 2 (OIL-281): items keep their area. `frameId` = {@link TEMPLATE_MAIN} (main-stage-local,
 * relative to the centre), the id of a template area (area-local), or null: in the tray with
 * `unplaced`, else a free object (main-stage-local, relative to the centre, like free-form).
 */
export const TemplateDataV2 = TemplateDataV1.extend({
  version: z.literal(2),
  areas: z.array(TemplateArea),
});
export type TemplateDataV2 = z.infer<typeof TemplateDataV2>;

export const TemplateData = z.union([TemplateDataV1, TemplateDataV2]);
export type TemplateData = z.infer<typeof TemplateData>;

/** Reads a template as version 2; a v1 `frameId: null` is the tray, anything else the stage. */
export function upgradeTemplate(t: TemplateData): TemplateDataV2 {
  if (t.version === 2) return t;
  return {
    ...t,
    version: 2,
    areas: [],
    items: t.items.map((i) =>
      i.frameId === null ? { ...i, ...IN_TRAY } : { ...i, frameId: TEMPLATE_MAIN },
    ),
  };
}

/** Item props key of editor groups (`editor/model/groups.ts`), remapped like an id. */
const GROUP_PROP = 'groupId';

function descendants(data: BoardData, roots: ReadonlySet<string>): Set<string> {
  const out = new Set(roots);
  let grew = true;
  while (grew) {
    grew = false;
    for (const i of Object.values(data.items)) {
      if (out.has(i.parentId ?? '') && !out.has(i.id)) {
        out.add(i.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Centre of the bounding box; 0/0 without points. */
const centreOf = (pts: readonly Point[]): Point => {
  if (pts.length === 0) return { x: 0, y: 0 };
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
};

const shiftEndpoint = (ep: Endpoint, dx: number, dy: number): Endpoint =>
  'elementId' in ep ? ep : { x: ep.x + dx, y: ep.y + dy };

/**
 * Collects a template from the selection (`ids`; empty = everything on the board): items with
 * their descendants, connections between them, channels sourced by them, mixes feeding them
 * (sends limited to the template's channels), power sources they use or represent, the
 * selected free-form elements and the non-main areas of its items (all of them for the whole
 * board). Main-stage items, free items and free-form elements become main-stage-local and
 * relative to the template centre (their bounding box); items in other areas stay area-local.
 */
export function extractTemplate(data: BoardData, ids: readonly string[]): TemplateDataV2 {
  const all = ids.length === 0;
  const picked = new Set(ids);
  const itemIds = all ? new Set(Object.keys(data.items)) : descendants(data, picked);
  const items = Object.values(data.items).filter((i) => itemIds.has(i.id));
  const freeform = Object.values(data.freeform).filter((f) => all || picked.has(f.id));
  const connections = Object.values(data.connections).filter(
    (c) => itemIds.has(c.from.itemId) && itemIds.has(c.to.itemId),
  );
  const channels = Object.values(data.channels).filter(
    (c) => all || itemIds.has(c.sourceItemId ?? ''),
  );
  const channelIds = new Set(channels.map((c) => c.id));
  const mixes = Object.values(data.mixes)
    .filter((m) => all || m.outputItemIds.some((id) => itemIds.has(id)))
    .map((m) => ({
      ...m,
      sends: m.sends.filter((s) => channelIds.has(s.channelId)),
      outputItemIds: m.outputItemIds.filter((id) => itemIds.has(id)),
    }));
  const usedSources = new Set(items.map((i) => i.power?.sourceId));
  const power = Object.values(data.power).filter(
    (p) => all || usedSources.has(p.id) || itemIds.has(p.itemId ?? ''),
  );

  const main = mainStage(data);
  const mx = main?.x ?? 0;
  const my = main?.y ?? 0;
  const onMain = (i: Item): boolean =>
    i.unplaced !== true && i.frameId !== null && i.frameId === main?.id;
  const isFree = (i: Item): boolean => i.frameId === null && i.unplaced !== true;
  const centre = centreOf([
    ...items.filter(onMain).map((i) => ({ x: i.x, y: i.y })),
    ...items.filter(isFree).map((i) => ({ x: i.x - mx, y: i.y - my })),
    ...freeform.flatMap((f) => [
      { x: f.x - mx, y: f.y - my },
      { x: f.x + f.w - mx, y: f.y + f.h - my },
    ]),
  ]);
  const dx = -centre.x;
  const dy = -centre.y;

  const used = new Set(items.map((i) => i.frameId));
  const areas = Object.values(data.frames)
    .filter((f) => f !== main && (all || used.has(f.id)))
    .map((f) => ({
      id: f.id,
      kind: f.kind,
      name: f.name,
      width: f.width,
      depth: f.depth,
      zones: f.zones,
      x: f.x - mx + dx,
      y: f.y - my + dy,
    }));

  const toTemplate = (i: Item): Item => {
    if (onMain(i)) return { ...i, frameId: TEMPLATE_MAIN, x: i.x + dx, y: i.y + dy };
    if (isFree(i)) return { ...i, x: i.x - mx + dx, y: i.y - my + dy };
    // in the tray, in another area, or in a missing area (position unknown → tray)
    return i.frameId === null || data.frames[i.frameId] !== undefined ? i : { ...i, ...IN_TRAY };
  };

  return {
    version: 2,
    areas,
    items: items.map(toTemplate),
    connections,
    channels,
    mixes,
    power,
    freeform: freeform.map((f) => {
      const out: FreeformElement = { ...f, x: f.x - mx + dx, y: f.y - my + dy };
      if (f.from) out.from = shiftEndpoint(f.from, -mx + dx, -my + dy);
      if (f.to) out.to = shiftEndpoint(f.to, -mx + dx, -my + dy);
      return out;
    }),
  };
}

/** Number of entities of a template (summary shown in lists). */
export function templateCounts(t: TemplateData): { items: number; channels: number } {
  return {
    items: t.items.length + t.freeform.length,
    channels: t.channels.length,
  };
}

export interface InstantiateOptions {
  /** Template centre in the board's main stage (main-stage-local metres). */
  at: Point;
  makeId?: () => string;
}

/** Entities to add to the board (all ids fresh, references remapped). */
export type TemplateEntities = Omit<TemplateDataV1, 'version'> & { frames: Frame[] };

/** Fresh frames for the template areas, laid out as in the template, right of the board's areas. */
function placeAreas(board: BoardData, areas: readonly TemplateArea[], mk: () => string): Frame[] {
  if (areas.length === 0) return [];
  const left = Math.min(...areas.map((a) => a.x));
  const top = Math.min(...areas.map((a) => a.y));
  const size = {
    width: Math.max(...areas.map((a) => a.x + a.width)) - left,
    depth: Math.max(...areas.map((a) => a.y + a.depth)) - top,
  };
  const spot = newAreaSpot(Object.values(board.frames), size);
  return areas.map((a) => ({
    id: mk(),
    kind: a.kind,
    name: a.name,
    x: spot.x + a.x - left,
    y: spot.y + a.y - top,
    width: a.width,
    depth: a.depth,
    zones: a.zones,
  }));
}

/**
 * Turns a template into new entities for `board` (decision U16, OIL-281): fresh ids with remapped
 * references, unknown layers → the kind's default layer, unknown power sources dropped, and
 * channel numbers shifted after the highest existing number if any of them is already taken.
 * Template areas become new frames next to the board's areas; main-stage items go into the
 * board's main stage around `at`; free items take the area under their centre (else stay free),
 * children and mounted items the area of their carrier.
 */
export function instantiateTemplate(
  board: BoardData,
  template: TemplateData,
  opts: InstantiateOptions,
): TemplateEntities {
  const t = upgradeTemplate(template);
  const mk = opts.makeId ?? (() => newId());
  const ids = new Map<string, string>();
  const groups = new Map<string, string>();
  const known = (id: string): string | undefined => ids.get(id);
  for (const e of [...t.items, ...t.freeform, ...t.channels, ...t.mixes, ...t.power])
    ids.set(e.id, mk());

  const main = mainStage(board);
  const fx = (main?.x ?? 0) + opts.at.x;
  const fy = (main?.y ?? 0) + opts.at.y;
  const frames = placeAreas(board, t.areas, mk);
  const areaIds = new Map(t.areas.map((a, n) => [a.id, frames[n]!]));
  const allFrames = {
    frames: { ...board.frames, ...Object.fromEntries(frames.map((f) => [f.id, f])) },
  };

  const byId = new Map(t.items.map((i) => [i.id, i]));
  const inTray = (i: Item): boolean => i.unplaced === true;
  const inArea = (i: Item): Frame | undefined => areaIds.get(i.frameId ?? '');
  const onMain = (i: Item): boolean =>
    !inTray(i) && inArea(i) === undefined && i.frameId !== null && main !== undefined;
  const world = (i: Item): Point => ({ x: fx + i.x, y: fy + i.y });
  const carrierOf = (i: Item): Item | undefined =>
    byId.get(i.mountedOn ?? '') ?? byId.get(i.parentId ?? '');
  /** Frame a free item lands in: its top carrier's, else the area under its own centre. */
  const freeFrame = (i: Item): string | null => {
    const seen = new Set([i]);
    let root = i;
    for (let c = carrierOf(root); c !== undefined && !seen.has(c); c = carrierOf(root)) {
      seen.add(c);
      root = c;
    }
    const own = inTray(root) ? i : root;
    const area = inArea(own);
    if (area) return area.id;
    if (onMain(own)) return main!.id;
    return placeAt(allFrames, world(own)).frameId;
  };
  const position = (i: Item): Pick<Item, 'frameId' | 'x' | 'y'> => {
    if (inTray(i)) return { frameId: null, x: i.x, y: i.y };
    const area = inArea(i);
    if (area) return { frameId: area.id, x: i.x, y: i.y };
    if (onMain(i)) return { frameId: main!.id, x: i.x + opts.at.x, y: i.y + opts.at.y };
    const p = world(i);
    const frameId = freeFrame(i);
    const f = allFrames.frames[frameId ?? ''];
    return { frameId, x: p.x - (f?.x ?? 0), y: p.y - (f?.y ?? 0) };
  };

  const items = t.items.map((i) => {
    const out: Item = {
      ...i,
      id: known(i.id)!,
      layerId:
        board.layers[i.layerId] !== undefined
          ? i.layerId
          : builtinLayerId(defaultLayerKeyFor(i.kind)),
      ...position(i),
      props: { ...i.props },
    };
    const parent = known(i.parentId ?? '');
    if (parent !== undefined) out.parentId = parent;
    else delete out.parentId;
    const carrier = known(i.mountedOn ?? '');
    if (carrier !== undefined) out.mountedOn = carrier;
    else delete out.mountedOn;
    const g = i.props[GROUP_PROP];
    if (typeof g === 'string') {
      if (!groups.has(g)) groups.set(g, mk());
      out.props[GROUP_PROP] = groups.get(g)!;
    }
    if (i.power?.sourceId !== undefined) {
      const src =
        known(i.power.sourceId) ?? (board.power[i.power.sourceId] ? i.power.sourceId : undefined);
      const power = { ...i.power };
      if (src !== undefined) power.sourceId = src;
      else delete power.sourceId;
      out.power = power;
    }
    return out;
  });

  // both ends must be part of the template (hand-made data may be inconsistent)
  const connections = t.connections
    .filter((c) => ids.has(c.from.itemId) && ids.has(c.to.itemId))
    .map((c) => {
      const out: Connection = {
        ...c,
        id: mk(),
        from: { ...c.from, itemId: known(c.from.itemId)! },
        to: { ...c.to, itemId: known(c.to.itemId)! },
      };
      const via = known(c.via?.itemId ?? '');
      if (via !== undefined) out.via = { ...c.via!, itemId: via };
      else delete out.via;
      return out;
    });

  const taken = new Set(Object.values(board.channels).map((c) => c.number));
  const clash = t.channels.some((c) => taken.has(c.number));
  const max = Math.max(0, ...taken);
  const min = Math.min(...t.channels.map((c) => c.number));
  const channels = t.channels.map((c) => {
    const out: Channel = {
      ...c,
      id: known(c.id)!,
      number: clash ? c.number - min + max + 1 : c.number,
    };
    const src = known(c.sourceItemId ?? '');
    if (src !== undefined) out.sourceItemId = src;
    else {
      delete out.sourceItemId;
      delete out.sourcePortId;
    }
    return out;
  });

  const mixes = t.mixes.map((m) => ({
    ...m,
    id: known(m.id)!,
    sends: m.sends.flatMap((s) => {
      const ch = known(s.channelId);
      return ch === undefined ? [] : [{ ...s, channelId: ch }];
    }),
    outputItemIds: m.outputItemIds.flatMap((id) => {
      const out = known(id);
      return out === undefined ? [] : [out];
    }),
  }));

  const power = t.power.map((p) => {
    const out: PowerSource = { ...p, id: known(p.id)! };
    const item = known(p.itemId ?? '');
    if (item !== undefined) out.itemId = item;
    else delete out.itemId;
    return out;
  });

  const moveEp = (ep: Endpoint | undefined): Endpoint | undefined => {
    if (ep === undefined) return undefined;
    if ('elementId' in ep) {
      const id = known(ep.elementId);
      return id === undefined ? undefined : { elementId: id };
    }
    return { x: ep.x + fx, y: ep.y + fy };
  };
  const freeform = t.freeform.map((f) => {
    const out: FreeformElement = { ...f, id: known(f.id)!, x: f.x + fx, y: f.y + fy };
    const from = moveEp(f.from);
    const to = moveEp(f.to);
    if (from !== undefined) out.from = from;
    else delete out.from;
    if (to !== undefined) out.to = to;
    else delete out.to;
    if (out.layerId !== undefined && board.layers[out.layerId] === undefined) delete out.layerId;
    return out;
  });

  return { frames, items, connections, channels, mixes, power, freeform };
}

/** Applies instantiated entities to a plain board object (server: new board from a template). */
export function applyTemplateEntities(board: BoardData, e: TemplateEntities): BoardData {
  const put = <T extends { id: string }>(rec: Record<string, T>, list: readonly T[]) => ({
    ...rec,
    ...Object.fromEntries(list.map((x) => [x.id, x])),
  });
  return {
    ...board,
    frames: put(board.frames, e.frames),
    items: put(board.items, e.items),
    connections: put(board.connections, e.connections),
    channels: put(board.channels, e.channels),
    mixes: put(board.mixes, e.mixes),
    power: put(board.power, e.power),
    freeform: put(board.freeform, e.freeform),
  };
}
